/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
    LayoutDashboard, Package, ArrowDownToLine, ArrowUpFromLine,
    BarChart3, Users
} from 'lucide-react';
import { supabase } from './lib/supabase';



// Services
import { inventoryService } from './services/inventoryService';

// Layout Components
import TitleBar from './components/layout/TitleBar';

import Sidebar from './components/layout/Sidebar';


// Context
import { NotificationProvider, useNotification } from './context/NotificationContext';

// View Components
import Dashboard from './components/views/Dashboard';
import Materials from './components/views/Materials';
import StockIn from './components/views/StockIn';
import StockOut from './components/views/StockOut';
import Analytics from './components/views/Analytics';
import Settings from './components/views/Settings';
import Members from './components/views/Members';

// Common Components
import AddMaterialModal from './components/common/AddMaterialModal';
import OperatorOnboardingModal from './components/common/OperatorOnboardingModal';
import Snackbar from './components/common/Snackbar';
import SplashScreen from './components/common/SplashScreen';
import UpdateBanner from './components/common/UpdateBanner';
import DocumentModal from './components/common/DocumentModal';

// Auth
import Login from './components/views/Login';

const isElectron = window && window.process && window.process.type;

function AppContent() {
    const [user, setUser] = useState(null);
    const [isSplashing, setIsSplashing] = useState(false);
    const [splashMessage, setSplashMessage] = useState('');
    const [activeTab, setActiveTab] = useState(localStorage.getItem('op-active-tab') || 'dashboard');
    const [materials, setMaterials] = useState([]);
    const [inboundLogs, setInboundLogs] = useState([]);
    const [stockOutBatches, setStockOutBatches] = useState([]);
    const [usageLogs, setUsageLogs] = useState([]);
    const [wasteLogs, setWasteLogs] = useState([]);
    const [efficiency, setEfficiency] = useState(null);
    const [productionLogs, setProductionLogs] = useState([]);
    const [showAddMaterial, setShowAddMaterial] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [activeDoc, setActiveDoc] = useState(null); // 'agreement' | 'documentation' | null
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
    const { notify } = useNotification();
    const [initialLoad, setInitialLoad] = useState(true);

    useEffect(() => {
        localStorage.setItem('op-active-tab', activeTab);
    }, [activeTab]);

    useEffect(() => {
        const saved = localStorage.getItem('op-user') || sessionStorage.getItem('op-user');
        if (saved) {
            try {
                setUser(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to parse saved user:', e);
            }
        }
        setInitialLoad(false);
    }, []);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        localStorage.setItem('theme', theme);
    }, [theme]);

    const refresh = useCallback(async () => {
        try {
            const [mats, logs, batches, usage, eff, waste, , prodLogs] = await Promise.all([
                inventoryService.getMaterials(),
                inventoryService.getInboundLogs(),
                inventoryService.getStockOutBatches(),
                inventoryService.getMaterialUsage(),
                inventoryService.getEfficiency(),
                inventoryService.getWasteLogs(),
                inventoryService.getWasteByMachine(),
                inventoryService.getProductionOutputs()
            ]);
            setMaterials(mats);
            setInboundLogs(logs);
            setStockOutBatches(batches);
            setUsageLogs(usage);
            setEfficiency(eff);
            setWasteLogs(waste);
            setProductionLogs(prodLogs);
        } catch (err) {
            console.error('Failed to refresh data:', err);
            notify('Error loading data from Supabase.', 'error');
        }
    }, [notify]);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            window.deferredPrompt = e;
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const handleLogin = (userData, rememberMe) => {
        setSplashMessage('Signing in...');
        setIsSplashing(true);
        setTimeout(() => {
            setUser(userData);
            
            // Persist based on Remember Me
            if (rememberMe) {
                localStorage.setItem('op-user', JSON.stringify(userData));
            } else {
                sessionStorage.setItem('op-user', JSON.stringify(userData));
            }

            setActiveDoc(null); // Clear any open documentation on login
            setIsSplashing(false);
            // Default tab based on role
            if (userData.role === 'operator') setActiveTab('stockOut');
            else setActiveTab('dashboard');
        }, 1200);
    };

    const handleLogout = () => {
        setSplashMessage('Signing out...');
        setIsSplashing(true);
        setTimeout(() => {
            setUser(null);
            localStorage.removeItem('op-user');
            sessionStorage.removeItem('op-user');
            localStorage.removeItem('op-active-tab');
            setIsSplashing(false);
            setActiveTab('dashboard');
        }, 1000);
    };

    useEffect(() => {
        if (!user) return;

        // Subscribe to changes for the current user
        const userSubscription = supabase
            .channel(`user-updates-${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'app_users',
                    filter: `id=eq.${user.id}`
                },
                (payload) => {
                    console.log('Realtime user update received:', payload.new);
                    setUser(payload.new);
                    notify(`Your profile has been updated (${payload.new.role || ''}).`, 'success');
                }
            )
            .subscribe();

        // 2. Subscribe to ALL public schema changes to refresh global data
        const globalSubscription = supabase
            .channel('global-updates')
            .on(
                'postgres_changes',
                {
                    event: '*', // Listen to INSERT, UPDATE, DELETE
                    schema: 'public'
                },
                (payload) => {
                    // Ignore app_users since we handle the current user specifically
                    // and Members.jsx handles the user list.
                    if (payload.table !== 'app_users') {
                        console.log(`Global update on ${payload.table}:`, payload.eventType);
                        refresh();
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(userSubscription);
            supabase.removeChannel(globalSubscription);
        };
    }, [user, notify, refresh]);

    const handleOnboardingComplete = async (machineId, shiftType) => {
        try {
            const updated = await inventoryService.updateUser(user.id, {
                machine_id: machineId,
                shift_type: shiftType
            });
            setUser(updated);
            notify('Profile updated successfully.', 'success');
        } catch (err) {
            notify('Error saving onboarding data.', 'error');
        }
    };

    const NAV = React.useMemo(() => {
        const fullNav = [
            { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'manager'] },
            { id: 'materials', icon: Package, label: 'Materials', roles: ['admin', 'manager'] },
            { id: 'stockIn', icon: ArrowDownToLine, label: 'Stock In', roles: ['admin', 'manager'] },
            { id: 'stockOut', icon: ArrowUpFromLine, label: 'Stock Out', roles: ['admin', 'manager', 'operator'] },
            { id: 'members', icon: Users, label: 'Members', roles: ['admin', 'manager'] },
            { id: 'analytics', icon: BarChart3, label: 'Analytics', roles: ['admin'] },
        ];
        return fullNav.filter(item => user && item.roles.includes(user.role));
    }, [user]);

    if (initialLoad) return <SplashScreen message="Initializing..." theme={theme} />;
    if (isSplashing) return <SplashScreen message={splashMessage} theme={theme} />;

    const renderContent = () => {
        if (!user) {
            return <Login onLogin={handleLogin} theme={theme} isElectron={isElectron} setActiveDoc={setActiveDoc} />;
        }

        const needsOnboarding = user.role === 'operator' && (!user.machine_id || !user.shift_type);

        return (
            <>
                {needsOnboarding && (
                    <OperatorOnboardingModal
                        user={user}
                        onComplete={handleOnboardingComplete}
                    />
                )}

                {showAddMaterial && (
                    <AddMaterialModal
                        user={user}
                        onClose={() => setShowAddMaterial(false)}
                        onSuccess={() => { refresh(); setShowAddMaterial(false); }}
                    />
                )}

                <div className="flex flex-1 overflow-hidden relative">
                    <Sidebar
                        navItems={NAV}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        onLogout={handleLogout}
                        user={user}
                        theme={theme}
                        setTheme={setTheme}
                    />

                    <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-7 relative w-full">
                        {activeTab === 'dashboard' && (
                            <Dashboard
                                materials={materials}
                                inboundLogs={inboundLogs}
                                stockOutBatches={stockOutBatches}
                                usageLogs={usageLogs}
                                efficiency={efficiency}
                                refresh={refresh}
                                setActiveTab={setActiveTab}
                                setShowAddMaterial={setShowAddMaterial}
                                setSelectedBatchId={setSelectedBatchId}
                            />
                        )}
                        {activeTab === 'materials' && (
                            <Materials
                                user={user}
                                materials={materials}
                                inboundLogs={inboundLogs}
                                usageLogs={usageLogs}
                                refresh={refresh}
                                setShowAddMaterial={setShowAddMaterial}
                            />
                        )}
                        {activeTab === 'stockIn' && (
                            <StockIn
                                materials={materials}
                                inboundLogs={inboundLogs}
                                refresh={refresh}
                                setActiveTab={setActiveTab}
                                user={user}
                            />
                        )}
                        {activeTab === 'stockOut' && (
                            <StockOut
                                materials={materials}
                                stockOutBatches={stockOutBatches}
                                refresh={refresh}
                                setActiveTab={setActiveTab}
                                user={user}
                                selectedBatchId={selectedBatchId}
                                setSelectedBatchId={setSelectedBatchId}
                            />
                        )}
                        {activeTab === 'analytics' && (
                            <Analytics
                                materials={materials}
                                stockOutBatches={stockOutBatches}
                                inboundLogs={inboundLogs}
                                usageLogs={usageLogs}
                                wasteLogs={wasteLogs}
                                productionLogs={productionLogs}
                                setActiveTab={setActiveTab}
                                setSelectedBatchId={setSelectedBatchId}
                                user={user}
                            />
                        )}
                        {activeTab === 'members' && (
                            <Members
                                user={user}
                                refresh={refresh}
                            />
                        )}
                        {activeTab === 'settings' && (
                            <Settings
                                theme={theme}
                                setTheme={setTheme}
                                isElectron={isElectron}
                                setActiveDoc={setActiveDoc}
                            />
                        )}
                    </main>
                </div>
                
            </>
        );
    };

    return (
        <div className="flex flex-col h-screen overflow-hidden" style={{ background: 'var(--surface)' }}>
            {/* Global UI Layer */}
            {isElectron && <TitleBar user={user} theme={theme} />}
            <DocumentModal type={activeDoc} onClose={() => setActiveDoc(null)} theme={theme} />
            <Snackbar />
            <UpdateBanner />

            {/* View Layer */}
            {renderContent()}
        </div>
    );
}

export default function App() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', backgroundColor: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', maxWidth: '400px', width: '90%' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 0.5rem 0' }}>Service Temporarily Unavailable</h2>
                <p style={{ color: '#4b5563', margin: 0 }}>We apologize for the inconvenience, but there is currently no communication with the database.</p>
            </div>
        </div>
    );
}
